/**
 * 指北针插件
 * 基于Three.js实现的四棱锥体指北针，支持相机朝向设置
 */
const THREE = qspace.THREE;

class CompassPlugin {
  constructor(container, options = {}) {
    this.container = container;
    this.options = Object.assign(
      {
        width: 100,
        height: 100,
        rotationAngle: 0,
        textOffsetX: 0, // 文字水平偏移
        textOffsetY: -20, // 文字垂直偏移，减少距离
        textContent: 'N', // 指北针文字内容，支持国际化
      },
      options,
    );

    this.scene = null;
    this.camera = null;
    this.renderer = null;
    this.controls = null;
    this.group = null;
    this.textMesh = null;
    this.cssTextElement = null;
    this.loadedFont = null;
    this.userRotation = new THREE.Quaternion(); // 用户设置的旋转
    this.northTip = null; // 北向尖端顶点
    this.init();
  }

  init() {
    // 确保THREE已经加载
    if (typeof THREE === 'undefined') {
      console.warn('THREE.js not available, retrying in 100ms...');
      setTimeout(() => this.init(), 100);
      return;
    }

    console.log('Initializing compass plugin with THREE:', THREE);

    this.createScene();
    this.createCompass();
    this.createLights();
    this.loadFont();
    this.setupControls();
    this.animate();
    this.fitToContainer();

    // 初始化时默认隐藏指北针和文字
    this.hide();
  }

  createScene() {
    // 创建场景
    this.scene = new THREE.Scene();

    // 创建相机 - 支持完整的3D旋转
    this.camera = new THREE.PerspectiveCamera(75, this.options.width / this.options.height, 0.1, 1000);
    // 相机初始位置在指北针前方，距离30单位
    this.camera.position.set(0, 0, 30);
    this.camera.up.set(0, 1, 0); // 设置相机的上方向
    this.camera.lookAt(0, 0, 0); // 始终看向指北针中心

    // 创建WebGL渲染器
    this.renderer = new THREE.WebGLRenderer({
      antialias: true,
      alpha: true, // 启用透明背景
    });
    this.renderer.setSize(this.options.width, this.options.height);
    this.renderer.setClearColor(0x000000, 0); // 设置透明背景
    this.container.appendChild(this.renderer.domElement);
  }

  createCompass() {
    // 创建双四棱锥体组
    this.group = new THREE.Group();

    // 上四棱锥
    const geometryUp = new THREE.CylinderGeometry(0, 10, 28, 4, 1);
    const materialUp = new THREE.MeshPhongMaterial({
      color: 0xff4d4d,
      shininess: 100,
    });
    const coneUp = new THREE.Mesh(geometryUp, materialUp);
    coneUp.position.y = 14;
    this.group.add(coneUp);

    // 下四棱锥
    const geometryDown = new THREE.CylinderGeometry(0, 10, 28, 4, 1);
    const materialDown = new THREE.MeshPhongMaterial({
      color: 0x00bfff, // 0xffffff
      shininess: 100,
    });
    const coneDown = new THREE.Mesh(geometryDown, materialDown);
    coneDown.position.y = -14;
    coneDown.rotation.x = Math.PI;
    this.group.add(coneDown);

    this.group.rotation.x = Math.PI / 2;
    this.scene.add(this.group);

    // 创建北向尖端顶点（红色四棱锥的顶部）
    this.northTip = new THREE.Vector3(0, 14, 0);

    if (this.options.rotationAngle) {
      this.scene.rotation.y = (-this.options.rotationAngle * Math.PI) / 180;
    }
  }

  createLights() {
    // 点光源
    const light = new THREE.PointLight(0xffffff, 1, 100);
    light.position.set(20, 20, 20);
    this.scene.add(light);

    // 环境光
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.8);
    this.scene.add(ambientLight);
  }

  loadFont() {
    // 创建文字元素
    this.createTextMesh();
  }

  createTextMesh() {
    // 移除之前的CSS文字元素
    if (this.cssTextElement) {
      this.container.removeChild(this.cssTextElement);
      this.cssTextElement = null;
    }

    // 创建简单的文字元素
    const textElement = document.createElement('div');
    textElement.className = 'compass-text';
    textElement.textContent = this.options.textContent || 'N';
    textElement.style.position = 'absolute';
    textElement.style.color = '#ffffff';
    textElement.style.fontSize = '12px';
    textElement.style.fontFamily = '"PingFangSC-Regular", "Microsoft YaHei", Arial, sans-serif';
    textElement.style.fontWeight = 'bold';
    textElement.style.textAlign = 'center';
    textElement.style.textShadow = '1px 1px 3px rgba(0, 0, 0, 0.8)';
    textElement.style.whiteSpace = 'nowrap';
    textElement.style.minWidth = '16px';
    textElement.style.minHeight = '16px';
    textElement.style.lineHeight = '1';
    textElement.style.pointerEvents = 'none';
    textElement.style.userSelect = 'none';
    textElement.style.left = '0px'; // 初始位置设为0
    textElement.style.top = '0px'; // 初始位置设为0
    textElement.style.zIndex = '1000';
    textElement.style.transition = 'none'; // 禁用过渡动画，确保实时跟随

    // 确保文字始终面向屏幕
    textElement.style.transformStyle = 'preserve-3d';
    textElement.style.backfaceVisibility = 'hidden';
    textElement.style.transform = 'translateZ(0)'; // 强制硬件加速

    this.container.appendChild(textElement);
    this.cssTextElement = textElement;
  }

  setupControls() {
    // 不使用OrbitControls，使用自定义鼠标控制
    this.controls = null;
  }

  animate() {
    const animate = () => {
      if (typeof requestAnimationFrame !== 'undefined') {
        // eslint-disable-next-line no-undef
        requestAnimationFrame(animate);
      }

      if (this.controls) {
        this.controls.update();
      }

      // 确保指北针组的世界矩阵是最新的
      this.group.updateMatrixWorld();

      // 更新文字位置到北向尖端
      this.updateTextToNorthTip();

      this.renderer.render(this.scene, this.camera);
    };
    animate();
  }

  // 更新文字内容
  updateTextContent(text) {
    if (this.cssTextElement) {
      // 如果没有传入文本，使用配置中的默认值
      const textToUse = text !== undefined ? text : this.options.textContent || 'N';
      this.cssTextElement.textContent = textToUse;
      // 同时更新配置，以便后续使用
      this.options.textContent = textToUse;
    }
  }

  // 设置文字位置
  setTextPosition(x, y) {
    if (this.cssTextElement) {
      // 使用translate3d确保硬件加速，同时保持文字面向屏幕
      this.cssTextElement.style.transform = `translate3d(${x}px, ${y}px, 0px)`;
    }
  }

  fitToContainer() {
    const size = Math.min(this.options.width, this.options.height);
    this.group.scale.set(size / 400, size / 400, size / 400);

    // 保持相机位置在容器中心，只调整距离
    this.camera.position.set(0, 0, 30 * (100 / size));
    this.camera.lookAt(0, 0, 0); // 始终看向指北针中心
    this.camera.updateProjectionMatrix();

    this.createTextMesh();
  }

  // 设置大小
  setSize(width, height) {
    this.options.width = width;
    this.options.height = height;
    this.renderer.setSize(width, height);
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
    this.fitToContainer();
  }

  // 销毁
  destroy() {
    if (this.renderer) {
      this.renderer.dispose();
      this.container.removeChild(this.renderer.domElement);
    }

    if (this.cssTextElement) {
      this.container.removeChild(this.cssTextElement);
    }

    if (this.scene) {
      this.scene.traverse((object) => {
        if (object.geometry) {
          object.geometry.dispose();
        }
        if (object.material) {
          if (Array.isArray(object.material)) {
            object.material.forEach((material) => material.dispose());
          } else {
            object.material.dispose();
          }
        }
      });
    }
  }

  // 显示指北针
  show() {
    if (this.renderer && this.renderer.domElement) {
      this.renderer.domElement.style.display = 'block';
    }
    if (this.group) {
      this.group.visible = true;
    }
    if (this.cssTextElement) {
      this.cssTextElement.style.display = 'block';
    }
  }

  // 隐藏指北针
  hide() {
    if (this.renderer && this.renderer.domElement) {
      this.renderer.domElement.style.display = 'none';
    }
    if (this.group) {
      this.group.visible = false;
    }
    if (this.cssTextElement) {
      this.cssTextElement.style.display = 'none';
    }
  }

  // 更新指北针姿态 - 只修改相机朝向，不动模型朝向
  updatePose(quaternion) {
    if (quaternion) {
      // 解析四元数
      let rotationQuaternion;
      if (quaternion instanceof THREE.Quaternion) {
        rotationQuaternion = quaternion.clone();
      } else if (Array.isArray(quaternion) && quaternion.length === 4) {
        rotationQuaternion = new THREE.Quaternion(quaternion[0], quaternion[1], quaternion[2], quaternion[3]);
      } else if (typeof quaternion === 'object' && quaternion.x !== undefined) {
        rotationQuaternion = new THREE.Quaternion(quaternion.x, quaternion.y, quaternion.z, quaternion.w);
      } else {
        return;
      }

      // 计算相机的新位置（围绕指北针旋转）
      const distance = 30; // 相机距离指北针的距离
      const direction = new THREE.Vector3(0, 0, 1); // 初始方向（Z轴正方向）
      direction.applyQuaternion(rotationQuaternion);

      // 设置相机位置
      this.camera.position.copy(direction.multiplyScalar(distance));

      // 相机始终看向指北针中心
      this.camera.lookAt(0, 0, 0);

      // 更新相机矩阵
      this.camera.updateMatrixWorld();

      // 更新文字位置
      this.updateTextToNorthTip();
    }
  }

  // 获取北向尖端的屏幕坐标
  getNorthTipScreenPosition() {
    if (!this.northTip || !this.camera || !this.renderer || !this.group) {
      return { x: 0, y: 0 };
    }

    // 获取指北针北向尖端的世界坐标
    const worldPosition = this.northTip.clone();
    worldPosition.applyMatrix4(this.group.matrixWorld);

    // 将3D世界坐标转换为屏幕坐标
    const screenPosition = worldPosition.clone();
    screenPosition.project(this.camera);

    // 转换为屏幕像素坐标
    const x = (screenPosition.x * 0.5 + 0.5) * this.options.width;
    const y = (screenPosition.y * -0.5 + 0.5) * this.options.height;

    // 计算指北针北向方向向量（从指北针中心指向北向尖端）
    const northDirection = new THREE.Vector3(0, 1, 0); // 指北针北向方向（Y轴正方向）
    northDirection.applyMatrix4(this.group.matrixWorld);

    // 将北向方向向量投影到屏幕空间
    const screenNorthDirection = northDirection.clone();
    screenNorthDirection.project(this.camera);

    // 计算北向方向在屏幕上的单位向量
    const screenNorthX = screenNorthDirection.x;
    const screenNorthY = -screenNorthDirection.y;

    // 归一化屏幕方向向量
    const length = Math.sqrt(screenNorthX * screenNorthX + screenNorthY * screenNorthY);
    if (length > 0) {
      const normalizedX = screenNorthX / length;
      const normalizedY = screenNorthY / length;

      // 计算偏移距离
      const offsetDistance = Math.sqrt(
        this.options.textOffsetX * this.options.textOffsetX + this.options.textOffsetY * this.options.textOffsetY,
      );

      // 使用固定的偏移距离，确保文字始终在指北针北向尖端的相对位置
      const offsetX = normalizedX * offsetDistance;
      const offsetY = normalizedY * offsetDistance;

      // 由于使用transform，需要减去文字元素本身的中心偏移
      const elementWidth = this.cssTextElement ? this.cssTextElement.offsetWidth : 16;
      const elementHeight = this.cssTextElement ? this.cssTextElement.offsetHeight : 16;
      const centerOffsetX = elementWidth / 2;
      const centerOffsetY = elementHeight / 2;

      return {
        x: x + offsetX - centerOffsetX,
        y: y + offsetY - centerOffsetY,
      };
    } else {
      // 如果无法计算方向，直接使用北向尖端位置
      const elementWidth = this.cssTextElement ? this.cssTextElement.offsetWidth : 16;
      const elementHeight = this.cssTextElement ? this.cssTextElement.offsetHeight : 16;
      const centerOffsetX = elementWidth / 2;
      const centerOffsetY = elementHeight / 2;

      return {
        x: x - centerOffsetX,
        y: y - centerOffsetY,
      };
    }
  }

  // 更新文字位置到北向尖端
  updateTextToNorthTip() {
    if (this.cssTextElement) {
      const screenPos = this.getNorthTipScreenPosition();

      // 确保文字位置在容器范围内
      const maxX = this.options.width - (this.cssTextElement.offsetWidth || 16);
      const maxY = this.options.height - (this.cssTextElement.offsetHeight || 16);

      const clampedX = Math.max(0, Math.min(screenPos.x, maxX));
      const clampedY = Math.max(0, Math.min(screenPos.y, maxY));

      this.setTextPosition(clampedX, clampedY);
    }
  }
}

// 导出插件
if (typeof module !== 'undefined' && module.exports) {
  module.exports = CompassPlugin;
} else if (typeof window !== 'undefined') {
  window.CompassPlugin = CompassPlugin;
}
